import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  CourierStatus,
  LatLng,
  Merchant,
  MerchantSchedule,
  BillingPlan,
  MerchantStatus,
  OrderStatus,
  OrderType,
  ParcelApproval,
  ParcelPayer,
  ParcelSize,
  PaymentMethod,
  Product,
  SettlementStatus,
  ProductCategory,
  ProductOptionGroup,
  UserRole,
  UserStatus,
  PermisosAdmin,
  RechargeStatus,
} from '@kick/types';
import { api } from './api';
import { useAuth } from './auth-store';

/** Producto tal como lo devuelve la API, con sus modificadores resueltos. */
export interface ProductoCompleto extends Product {
  optionGroups: ProductOptionGroup[];
}

export interface UsuarioComercio {
  id: string;
  username: string;
  fullName: string;
  phone: string | null;
  email: string | null;
  status: string;
  rolEnComercio: 'OWNER' | 'STAFF';
  lastLoginAt: string | null;
}

/**
 * Claves de caché.
 *
 * Agrupadas por comercio para poder invalidar todo lo suyo de una vez, y
 * para que cambiar de comercio no muestre por un instante los datos del anterior.
 */
export const claves = {
  comercio: (id: string) => ['comercio', id] as const,
  secciones: (id: string) => ['comercio', id, 'secciones'] as const,
  productos: (id: string) => ['comercio', id, 'productos'] as const,
  producto: (id: string, productoId: string) =>
    ['comercio', id, 'productos', productoId] as const,
  horarios: (id: string) => ['comercio', id, 'horarios'] as const,
  usuarios: (id: string) => ['comercio', id, 'usuarios'] as const,
  categorias: () => ['categorias'] as const,
};

/** El comercio del usuario autenticado; lanza si la sesión no tiene uno. */
export function useMerchantId(explicito?: string): string {
  const propio = useAuth((s) => s.merchantId);
  // El administrador abre la ficha de cualquier comercio por la URL; el
  // comerciante siempre trabaja sobre el suyo.
  const merchantId = explicito ?? propio;
  if (!merchantId) {
    throw new Error('Esta cuenta no está asociada a ningún comercio');
  }
  return merchantId;
}

// ── Comercio ──────────────────────────────────────────────

export function useComercio(merchantId: string) {
  return useQuery({
    queryKey: claves.comercio(merchantId),
    queryFn: async () => (await api.get<Merchant>(`/merchants/${merchantId}`)).data,
  });
}

export function useActualizarComercio(merchantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (datos: Partial<Merchant>) =>
      (await api.patch<Merchant>(`/merchants/${merchantId}`, datos)).data,
    onSuccess: (comercio) => qc.setQueryData(claves.comercio(merchantId), comercio),
  });
}

export function useAlternarApertura(merchantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (isOpenNow: boolean) =>
      (await api.patch<Merchant>(`/merchants/${merchantId}/apertura`, { isOpenNow })).data,
    onSuccess: (comercio) => qc.setQueryData(claves.comercio(merchantId), comercio),
  });
}

export function useSubirImagenComercio(merchantId: string, tipo: 'logo' | 'portada') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (archivo: File) => {
      const datos = new FormData();
      datos.append('imagen', archivo);
      const { data } = await api.post<Merchant>(`/merchants/${merchantId}/${tipo}`, datos, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
    onSuccess: (comercio) => qc.setQueryData(claves.comercio(merchantId), comercio),
  });
}

// ── Horarios ──────────────────────────────────────────────

export function useHorarios(merchantId: string) {
  return useQuery({
    queryKey: claves.horarios(merchantId),
    queryFn: async () =>
      (await api.get<MerchantSchedule[]>(`/merchants/${merchantId}/horarios`)).data,
  });
}

export function useGuardarHorarios(merchantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (schedules: Omit<MerchantSchedule, 'id' | 'merchantId'>[]) =>
      (await api.put<MerchantSchedule[]>(`/merchants/${merchantId}/horarios`, { schedules })).data,
    onSuccess: (horarios) => {
      qc.setQueryData(claves.horarios(merchantId), horarios);
      // El horario decide si el comercio aparece abierto en la tienda.
      void qc.invalidateQueries({ queryKey: claves.comercio(merchantId) });
    },
  });
}

// ── Secciones del menú ────────────────────────────────────

export function useSecciones(merchantId: string) {
  return useQuery({
    queryKey: claves.secciones(merchantId),
    queryFn: async () =>
      (await api.get<ProductCategory[]>(`/merchants/${merchantId}/secciones`)).data,
  });
}

export function useCrearSeccion(merchantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (datos: { name: string; sortOrder?: number }) =>
      (await api.post<ProductCategory>(`/merchants/${merchantId}/secciones`, datos)).data,
    onSuccess: () => invalidarMenu(qc, merchantId),
  });
}

export function useActualizarSeccion(merchantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...datos }: { id: string } & Partial<ProductCategory>) =>
      (await api.patch<ProductCategory>(`/merchants/${merchantId}/secciones/${id}`, datos)).data,
    onSuccess: () => invalidarMenu(qc, merchantId),
  });
}

export function useEliminarSeccion(merchantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/merchants/${merchantId}/secciones/${id}`);
    },
    onSuccess: () => invalidarMenu(qc, merchantId),
  });
}

export function useReordenarSecciones(merchantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) =>
      (await api.put<ProductCategory[]>(`/merchants/${merchantId}/secciones/orden`, { ids })).data,
    /**
     * Actualización optimista: al arrastrar, la lista debe reacomodarse al
     * instante. Si el servidor falla, se restaura el orden anterior.
     */
    onMutate: async (ids) => {
      await qc.cancelQueries({ queryKey: claves.secciones(merchantId) });
      const previas = qc.getQueryData<ProductCategory[]>(claves.secciones(merchantId));
      if (previas) {
        const porId = new Map(previas.map((s) => [s.id, s]));
        qc.setQueryData(
          claves.secciones(merchantId),
          ids.map((id, i) => ({ ...porId.get(id)!, sortOrder: i })),
        );
      }
      return { previas };
    },
    onError: (_error, _ids, contexto) => {
      if (contexto?.previas) qc.setQueryData(claves.secciones(merchantId), contexto.previas);
    },
    onSettled: () => invalidarMenu(qc, merchantId),
  });
}

// ── Productos ─────────────────────────────────────────────

export function useProductos(merchantId: string) {
  return useQuery({
    queryKey: claves.productos(merchantId),
    queryFn: async () => (await api.get<Product[]>(`/merchants/${merchantId}/productos`)).data,
  });
}

export function useProducto(merchantId: string, productoId: string | null) {
  return useQuery({
    queryKey: claves.producto(merchantId, productoId ?? ''),
    queryFn: async () =>
      (await api.get<ProductoCompleto>(`/merchants/${merchantId}/productos/${productoId}`)).data,
    enabled: !!productoId,
  });
}

export function useGuardarProducto(merchantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...datos }: { id?: string } & Record<string, unknown>) => {
      const ruta = `/merchants/${merchantId}/productos${id ? `/${id}` : ''}`;
      const { data } = id
        ? await api.patch<ProductoCompleto>(ruta, datos)
        : await api.post<ProductoCompleto>(ruta, datos);
      return data;
    },
    onSuccess: () => invalidarMenu(qc, merchantId),
  });
}

export function useDisponibilidadProducto(merchantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, isAvailable }: { id: string; isAvailable: boolean }) =>
      (
        await api.patch<Product>(`/merchants/${merchantId}/productos/${id}/disponibilidad`, {
          isAvailable,
        })
      ).data,
    /** Marcar agotado ocurre con el local lleno: la respuesta debe ser inmediata. */
    onMutate: async ({ id, isAvailable }) => {
      await qc.cancelQueries({ queryKey: claves.productos(merchantId) });
      const previos = qc.getQueryData<Product[]>(claves.productos(merchantId));
      qc.setQueryData<Product[]>(claves.productos(merchantId), (actuales) =>
        actuales?.map((p) => (p.id === id ? { ...p, isAvailable } : p)),
      );
      return { previos };
    },
    onError: (_e, _v, contexto) => {
      if (contexto?.previos) qc.setQueryData(claves.productos(merchantId), contexto.previos);
    },
    onSettled: () => invalidarMenu(qc, merchantId),
  });
}

export function useDuplicarProducto(merchantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post<ProductoCompleto>(`/merchants/${merchantId}/productos/${id}/duplicar`)).data,
    onSuccess: () => invalidarMenu(qc, merchantId),
  });
}

export function useEliminarProducto(merchantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/merchants/${merchantId}/productos/${id}`);
    },
    onSuccess: () => invalidarMenu(qc, merchantId),
  });
}

export function useReordenarProductos(merchantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) =>
      (await api.put<Product[]>(`/merchants/${merchantId}/productos/orden`, { ids })).data,
    onSettled: () => invalidarMenu(qc, merchantId),
  });
}

export function useSubirImagenProducto(merchantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, archivo }: { id: string; archivo: File }) => {
      const datos = new FormData();
      datos.append('imagen', archivo);
      const { data } = await api.post<Product>(
        `/merchants/${merchantId}/productos/${id}/imagen`,
        datos,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return data;
    },
    onSuccess: () => invalidarMenu(qc, merchantId),
  });
}

// ── Usuarios del comercio ─────────────────────────────────

export function useUsuariosComercio(merchantId: string) {
  return useQuery({
    queryKey: claves.usuarios(merchantId),
    queryFn: async () =>
      (await api.get<UsuarioComercio[]>(`/merchants/${merchantId}/usuarios`)).data,
  });
}

export function useCrearUsuarioComercio(merchantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (datos: {
      username: string;
      fullName: string;
      phone: string;
      email?: string;
      role: 'OWNER' | 'STAFF';
    }) =>
      (
        await api.post<{ username: string; passwordTemporal: string }>(
          `/merchants/${merchantId}/usuarios`,
          datos,
        )
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: claves.usuarios(merchantId) }),
  });
}

// ── Catálogo de la plataforma ─────────────────────────────

export function useCategoriasPlataforma() {
  return useQuery({
    queryKey: claves.categorias(),
    queryFn: async () =>
      (
        await api.get<{ id: string; name: string; slug: string; icon: string | null }[]>(
          '/public/categorias',
        )
      ).data,
    // Las categorías casi no cambian: no vale la pena repreguntar.
    staleTime: 30 * 60 * 1000,
  });
}

/** Un cambio en el menú puede afectar secciones y productos a la vez. */
function invalidarMenu(qc: QueryClient, merchantId: string): void {
  void qc.invalidateQueries({ queryKey: claves.secciones(merchantId) });
  void qc.invalidateQueries({ queryKey: claves.productos(merchantId) });
}

// ── Zonas de cobertura (administración) ───────────────────

export interface ZonaAdmin {
  id: string;
  name: string;
  city: string;
  area: LatLng[];
  baseFee: number;
  perKmFee: number;
  freeDeliveryOver: number | null;
  isActive: boolean;
  /** Cuántos comercios la tienen asignada. */
  merchantCount: number;
}

export function useZonas() {
  return useQuery({
    queryKey: ['zonas'],
    queryFn: async () => (await api.get<ZonaAdmin[]>('/zonas')).data,
  });
}

export function useCrearZona() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (datos: Record<string, unknown>) =>
      (await api.post<ZonaAdmin>('/zonas', datos)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['zonas'] }),
  });
}

export function useActualizarZona() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...datos }: { id: string } & Record<string, unknown>) =>
      (await api.patch<ZonaAdmin>(`/zonas/${id}`, datos)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['zonas'] }),
  });
}

export function useEliminarZona() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.delete<{ eliminada: boolean; desactivada: boolean }>(`/zonas/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['zonas'] }),
  });
}

/** Avisa si el contorno se cruza consigo mismo, antes de guardarlo. */
export function useRevisarPoligono() {
  return useMutation({
    mutationFn: async (area: LatLng[]) =>
      (await api.post<{ valido: boolean; motivo: string | null }>('/zonas/revisar-poligono', { area }))
        .data,
  });
}

// ── Pedidos ───────────────────────────────────────────────

export interface LineaPedido {
  id: string;
  productId: string | null;
  productName: string;
  unitPrice: number;
  quantity: number;
  options: { groupName: string; optionName: string; priceDelta: number }[];
  notes: string | null;
  lineTotal: number;
}

export interface PedidoComercio {
  id: string;
  code: string;
  status: OrderStatus;
  subtotal: number;
  deliveryFee: number;
  serviceFee: number;
  discountTotal: number;
  tip: number;
  total: number;
  commissionAmount: number;
  /** Pedido por lista: en qué va la revisión; nulo en un pedido normal. */
  listStage: string | null;
  budgetCap: number | null;
  paymentMethod: PaymentMethod;
  paymentStatus: string;
  courierId: string | null;
  deliveryAddress: {
    label: string;
    addressLine: string;
    details: string | null;
    neighborhood: string | null;
    city: string;
    contactPhone: string | null;
  };
  /** Punto exacto de entrega: con él se calcula cuánto le falta al domiciliario. */
  deliveryLocation: LatLng;
  distanceMeters: number;
  customerNotes: string | null;
  cancelReason: string | null;
  estimatedReadyAt: string | null;
  estimatedDeliveryAt: string | null;
  placedAt: string | null;
  acceptedAt: string | null;
  readyAt: string | null;
  pickedUpAt: string | null;
  deliveredAt: string | null;
  createdAt: string;
  items: LineaPedido[];
}

/**
 * Pedidos vivos del comercio.
 *
 * Con el canal en vivo abierto no se consulta por reloj: los eventos invalidan
 * esta consulta y llega el estado nuevo. El sondeo se mantiene **solo** como
 * red de seguridad mientras el socket esté caído — un tablero que dejó de
 * recibir sin avisar haría creer al comercio que no entran pedidos.
 */
export function usePedidosActivos(merchantId: string, enVivo = false) {
  return useQuery({
    queryKey: ['pedidos', merchantId, 'activos'],
    queryFn: async () =>
      (await api.get<PedidoComercio[]>(`/pedidos/comercio/${merchantId}/activos`)).data,
    refetchInterval: enVivo ? false : 10_000,
    // También al volver a la pestaña: el local no mira el panel todo el rato.
    refetchOnWindowFocus: true,
  });
}

export function useHistorialPedidos(merchantId: string) {
  return useQuery({
    queryKey: ['pedidos', merchantId, 'historial'],
    queryFn: async () =>
      (
        await api.get<{ data: PedidoComercio[]; hasMore: boolean }>(
          `/pedidos/comercio/${merchantId}?limit=30`,
        )
      ).data,
  });
}

// ── Administración ────────────────────────────────────────

export interface Ajustes {
  commissionRate: number;
  /** El valor de la tarifa de lluvia; se cobra solo con `rainFeeActive`. */
  serviceFee: number;
  rainFeeActive: boolean;
  minOrderAmount: number;
  assignmentRadiusMeters: number;
  assignmentMaxRadiusMeters: number;
  assignmentOfferTimeoutSeconds: number;
  assignmentMaxAttempts: number;
  tipPresets: number[];
  /** Ganancia del domiciliario: base fija más adicional por kilómetro. */
  courierBaseFee: number;
  courierPerKmFee: number;
  /** Pedidos que un mensajero puede llevar a la vez. */
  maxActiveOrdersPerCourier: number;
  /** El interruptor de la plataforma y el aviso que ve el cliente apagada. */
  platformOpen: boolean;
  platformClosedMessage: string;
  /** Saldo prepago: % del domicilio que se descuenta por entrega y a dónde recargar. */
  courierBalanceCutPercent: number;
  /** Recargo por pedir a un comercio no aliado, en centavos. */
  nonPartnerFee: number;
  listReviewAlertMinutes: number;
  rechargeAccountInfo: string;
  /** Clasificación: umbral premium del cliente y bono del mensajero. */
  premiumOrdersPerMonth: number;
  premiumDiscountPercent: number;
  courierBonusDeliveries: number;
  courierBonusAmount: number;
}

export interface CategoriaAdmin {
  id: string;
  name: string;
  slug: string;
  icon: string | null;
  imageUrl: string | null;
  sortOrder: number;
  isActive: boolean;
  merchantCount: number;
}

export interface ComercioAdmin {
  id: string;
  name: string;
  slug: string;
  categoryId: string;
  status: string;
  isOpenNow: boolean;
  /** Pedido por lista activado para este comercio. */
  listOrdering: boolean;
  commissionRate: string;
  addressLine: string;
  city: string;
  phone: string | null;
  email: string | null;
  logoUrl: string | null;
  minOrderAmount: number;
  avgPrepMinutes: number;
  ratingAvg: string;
  ratingCount: number;
  zoneId: string | null;
  createdAt: string;
}

export interface UsuarioAdmin {
  id: string;
  username: string;
  fullName: string;
  email: string | null;
  phone: string | null;
  role: UserRole;
  status: UserStatus;
  mustChangePassword: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  merchantName: string | null;
}

export interface DomiciliarioAdmin {
  id: string;
  userId: string;
  fullName: string;
  username: string;
  phone: string | null;
  userStatus: UserStatus;
  vehicleType: string;
  plate: string | null;
  documentId: string;
  documentPhotoUrl: string | null;
  licensePhotoUrl: string | null;
  status: CourierStatus;
  isVerified: boolean;
  ratingAvg: string;
  ratingCount: number;
  /** Cuántos pedidos lleva encima ahora mismo (multi-asignación). */
  activeOrders: number;
  /** Saldo prepago en centavos y recargas esperando revisión. */
  balance: number;
  pendingRecharges: number;
  locationUpdatedAt: string | null;
  createdAt: string;
  entregas: number;
}

export interface PedidoOperacion {
  id: string;
  code: string;
  status: OrderStatus;
  total: number;
  distanceMeters: number;
  deliveryAddress: { addressLine: string; neighborhood: string | null };
  placedAt: string | null;
  acceptedAt: string | null;
  estimatedDeliveryAt: string | null;
  type: OrderType;
  /** Nulos en una encomienda: no hay comercio. */
  merchantId: string | null;
  merchantName: string | null;
  /** De dónde sale el encargo: el comercio, o quien manda el paquete. */
  pickupName: string;
  courierId: string | null;
  courierName: string | null;
  alertas: ('SIN_ACEPTAR' | 'SIN_DOMICILIARIO' | 'ENTREGA_DEMORADA')[];
}

export interface ResumenOperacion {
  pedidos: { activos: number; sinDomiciliario: number; sinAceptar: number };
  flota: { enTurno: number; libres: number; ocupados: number };
  comercios: { activos: number; abiertos: number };
  hoy: {
    pedidos: number;
    entregados: number;
    cancelados: number;
    gmv: number;
    comisiones: number;
  };
}

export interface ResumenInforme {
  pedidos: number;
  entregados: number;
  cancelados: number;
  tasaCancelacion: number;
  gmv: number;
  ventas: number;
  comisiones: number;
  domicilios: number;
  propinas: number;
  ticketPromedio: number;
  minutosMedios: number;
}

export interface DiaInforme {
  dia: string;
  pedidos: number;
  ventas: number;
  comisiones: number;
  cancelados: number;
}

export interface InformeComercio {
  periodo: { desde: string; hasta: string };
  resumen: ResumenInforme;
  porDia: DiaInforme[];
  topProductos: { productName: string; unidades: number; ingresos: number }[];
  horasPico: { hora: number; pedidos: number }[];
}

export interface InformePlataforma {
  periodo: { desde: string; hasta: string };
  resumen: ResumenInforme;
  porDia: DiaInforme[];
  porComercio: {
    merchantId: string;
    merchantName: string;
    pedidos: number;
    ventas: number;
    comisiones: number;
    cancelados: number;
  }[];
  porZona: {
    zoneId: string;
    zoneName: string;
    pedidos: number;
    domicilios: number;
    distanciaMedia: number;
  }[];
  porDomiciliario: {
    courierId: string;
    courierName: string;
    entregas: number;
    ganado: number;
    propinas: number;
    distancia: number;
  }[];
}

export interface PreviaLiquidacion {
  periodo: { desde: string; hasta: string };
  comercios: {
    merchantId: string;
    merchantName: string;
    ordersCount: number;
    grossAmount: number;
    commissionAmount: number;
    monthlyFee: number;
    deliveryCharges: number;
    deliveriesRequested: number;
    /** Lo que el comercio le debe a la plataforma, no al revés. */
    amountDue: number;
  }[];
  domiciliarios: {
    courierId: string;
    courierName: string;
    deliveriesCount: number;
  /** Bono por llegar al umbral de entregas; cero si no llegó. */
  bonusAmount: number;
    baseAmount: number;
    distanceBonus: number;
    tips: number;
    totalAmount: number;
  }[];
  totales: {
    gmv: number;
    ventasComercios: number;
    comisiones: number;
    mensualidades: number;
    /** Lo que los comercios le deben a la plataforma en el período. */
    cobrosAComercios: number;
    domicilios: number;
    servicio: number;
    propinas: number;
    pagoDomiciliarios: number;
    ingresos: number;
    costoReparto: number;
    margen: number;
  };
}

export interface LiquidacionComercio {
  id: string;
  merchantId: string;
  merchantName: string;
  periodStart: string;
  periodEnd: string;
  ordersCount: number;
  grossAmount: number;
  commissionAmount: number;
  monthlyFee: number;
  deliveryCharges: number;
  deliveriesRequested: number;
  amountDue: number;
  paidAmount: number;
  dueDate: string | null;
  status: string;
  paidAt: string | null;
  notes: string | null;
}

export interface LiquidacionDomiciliario {
  id: string;
  courierId: string;
  courierName: string;
  periodStart: string;
  periodEnd: string;
  deliveriesCount: number;
  /** Bono por llegar al umbral de entregas; cero si no llegó. */
  bonusAmount: number;
  totalAmount: number;
  status: string;
  paidAt: string | null;
}

/** Rango de fechas en el formato que espera la API. */
export interface Rango {
  desde: Date;
  hasta: Date;
}

const comoParams = (rango: Rango): Record<string, string> => ({
  desde: rango.desde.toISOString(),
  hasta: rango.hasta.toISOString(),
});

export function useInformeComercio(merchantId: string, rango: Rango) {
  return useQuery({
    queryKey: ['informe', merchantId, rango.desde.toISOString(), rango.hasta.toISOString()],
    queryFn: async () =>
      (
        await api.get<InformeComercio>(`/merchants/${merchantId}/informes`, {
          params: comoParams(rango),
        })
      ).data,
  });
}

export function useInformePlataforma(rango: Rango) {
  return useQuery({
    queryKey: ['informe', 'plataforma', rango.desde.toISOString(), rango.hasta.toISOString()],
    queryFn: async () =>
      (await api.get<InformePlataforma>('/admin/informes', { params: comoParams(rango) })).data,
  });
}

/**
 * Descarga el Excel del informe.
 *
 * Se pide con Axios y no con un enlace directo porque la ruta exige el token
 * de sesión, y un `<a href>` no lo lleva.
 */
export async function descargarExcel(ruta: string, rango: Rango, nombre: string): Promise<void> {
  const { data } = await api.get<Blob>(ruta, {
    params: comoParams(rango),
    responseType: 'blob',
  });

  const url = URL.createObjectURL(data);
  const enlace = document.createElement('a');
  enlace.href = url;
  enlace.download = nombre;
  enlace.click();
  URL.revokeObjectURL(url);
}

export function usePreviaLiquidacion(rango: Rango, habilitado = true) {
  return useQuery({
    queryKey: ['liquidacion', 'previa', rango.desde.toISOString(), rango.hasta.toISOString()],
    queryFn: async () =>
      (await api.get<PreviaLiquidacion>('/admin/liquidaciones/previa', { params: comoParams(rango) }))
        .data,
    enabled: habilitado,
  });
}

export function useLiquidaciones() {
  const comercios = useQuery({
    queryKey: ['liquidacion', 'comercios'],
    queryFn: async () =>
      (await api.get<LiquidacionComercio[]>('/admin/liquidaciones/comercios')).data,
  });
  const domiciliarios = useQuery({
    queryKey: ['liquidacion', 'domiciliarios'],
    queryFn: async () =>
      (await api.get<LiquidacionDomiciliario[]>('/admin/liquidaciones/domiciliarios')).data,
  });
  return { comercios, domiciliarios };
}

/** El corte general: congela lo que cada comercio lleve acumulado. */
export function useCerrarPeriodo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (await api.post<{ comercios: number; total: number }>('/admin/liquidaciones/cerrar')).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['liquidacion'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'cobros'] });
      void qc.invalidateQueries({ queryKey: ['comercio'] });
    },
  });
}

export function usePagarLiquidacion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      tipo,
      id,
      notes,
    }: {
      tipo: 'comercios' | 'domiciliarios';
      id: string;
      notes?: string;
    }) => (await api.post(`/admin/liquidaciones/${tipo}/${id}/pagar`, { notes })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['liquidacion'] }),
  });
}

export interface RenglonAuditoria {
  id: string;
  action: string;
  entityType: string;
  entityId: string | null;
  changes: { before?: unknown; after?: unknown } | null;
  ip: string | null;
  createdAt: string;
  actorId: string | null;
  actorRole: UserRole | null;
  actorName: string | null;
  actorUsername: string | null;
}

export interface Credenciales {
  username: string;
  passwordTemporal: string;
}

/** Claves de la administración, para invalidar por sección. */
export const clavesAdmin = {
  ajustes: () => ['admin', 'ajustes'] as const,
  categorias: () => ['admin', 'categorias'] as const,
  comercios: (filtros: Record<string, unknown>) => ['admin', 'comercios', filtros] as const,
  usuarios: (filtros: Record<string, unknown>) => ['admin', 'usuarios', filtros] as const,
  domiciliarios: (filtros: Record<string, unknown>) =>
    ['admin', 'domiciliarios', filtros] as const,
  operacion: () => ['admin', 'operacion'] as const,
  auditoria: (filtros: Record<string, unknown>) => ['admin', 'auditoria', filtros] as const,
};

export function useAjustes() {
  return useQuery({
    queryKey: clavesAdmin.ajustes(),
    queryFn: async () => (await api.get<Ajustes>('/admin/ajustes')).data,
  });
}

export function useGuardarAjustes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (datos: Partial<Ajustes>) =>
      (await api.patch<Ajustes>('/admin/ajustes', datos)).data,
    onSuccess: (ajustes) => qc.setQueryData(clavesAdmin.ajustes(), ajustes),
  });
}

export function useCategoriasAdmin() {
  return useQuery({
    queryKey: clavesAdmin.categorias(),
    queryFn: async () => (await api.get<CategoriaAdmin[]>('/admin/categorias')).data,
  });
}

export function useGuardarCategoria() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...datos }: { id?: string } & Record<string, unknown>) => {
      const { data } = id
        ? await api.patch<CategoriaAdmin>(`/admin/categorias/${id}`, datos)
        : await api.post<CategoriaAdmin>('/admin/categorias', datos);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: clavesAdmin.categorias() }),
  });
}

export function useEliminarCategoria() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (
        await api.delete<{ eliminada: boolean; desactivada: boolean; merchantCount: number }>(
          `/admin/categorias/${id}`,
        )
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: clavesAdmin.categorias() }),
  });
}

export function useComerciosAdmin(filtros: { q?: string; status?: string }) {
  return useQuery({
    queryKey: clavesAdmin.comercios(filtros),
    queryFn: async () => {
      const params = Object.fromEntries(
        Object.entries({ ...filtros, limit: 100 }).filter(([, v]) => v !== undefined && v !== ''),
      );
      return (await api.get<{ data: ComercioAdmin[]; hasMore: boolean }>('/merchants', { params }))
        .data;
    },
  });
}

export function useCrearComercio() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (datos: Record<string, unknown>) =>
      (
        await api.post<{ comercio: ComercioAdmin; credenciales?: Credenciales }>(
          '/merchants',
          datos,
        )
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'comercios'] }),
  });
}

export function useCambiarEstadoComercio() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) =>
      (await api.patch<ComercioAdmin>(`/merchants/${id}/estado`, { status })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'comercios'] }),
  });
}

export function useActualizarComercioAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...datos }: { id: string } & Record<string, unknown>) =>
      (await api.patch<ComercioAdmin>(`/merchants/${id}`, datos)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'comercios'] }),
  });
}

export function useUsuariosAdmin(filtros: { q?: string; role?: string; status?: string }) {
  return useQuery({
    queryKey: clavesAdmin.usuarios(filtros),
    queryFn: async () => {
      const params = Object.fromEntries(
        Object.entries({ ...filtros, limit: 50 }).filter(([, v]) => v !== undefined && v !== ''),
      );
      return (
        await api.get<{ data: UsuarioAdmin[]; hasMore: boolean }>('/admin/usuarios', { params })
      ).data;
    },
  });
}

export function useCambiarEstadoUsuario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status, reason }: { id: string; status: string; reason?: string }) =>
      (await api.patch(`/admin/usuarios/${id}/estado`, { status, reason })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'usuarios'] }),
  });
}

/** Restablecer la clave de un domiciliario (por su id de domiciliario, no de usuario). */
export function useRestablecerPasswordDomiciliario() {
  return useMutation({
    mutationFn: async (courierId: string) =>
      (await api.post<Credenciales>(`/admin/domiciliarios/${courierId}/restablecer-password`)).data,
  });
}

export function useRestablecerPassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post<Credenciales>(`/admin/usuarios/${id}/restablecer-password`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'usuarios'] }),
  });
}

export function useDomiciliariosAdmin(filtros: { q?: string; verificados?: string }) {
  return useQuery({
    queryKey: clavesAdmin.domiciliarios(filtros),
    queryFn: async () => {
      const params = Object.fromEntries(
        Object.entries(filtros).filter(([, v]) => v !== undefined && v !== ''),
      );
      return (await api.get<DomiciliarioAdmin[]>('/admin/domiciliarios', { params })).data;
    },
  });
}

export function useCrearDomiciliario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (datos: Record<string, unknown>) =>
      (
        await api.post<{ courier: DomiciliarioAdmin; credenciales: Credenciales }>(
          '/admin/domiciliarios',
          datos,
        )
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'domiciliarios'] }),
  });
}

export interface CuentaDomiciliario {
  id: string;
  courierId: string;
  type: string;
  bankName: string | null;
  accountKind: string | null;
  accountNumber: string;
  holderName: string;
  holderDocument: string | null;
  qrUrl: string | null;
  isDefault: boolean;
  isActive: boolean;
}

/** Dónde recibe el domiciliario las transferencias de los clientes. */
export function useCuentasDomiciliario(courierId: string | null) {
  return useQuery({
    queryKey: ['admin', 'cuentas', courierId],
    queryFn: async () =>
      (await api.get<CuentaDomiciliario[]>(`/pagos/domiciliarios/${courierId}/cuentas`)).data,
    enabled: !!courierId,
  });
}

export function useGuardarCuenta(courierId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...datos }: { id?: string } & Record<string, unknown>) => {
      const ruta = `/pagos/domiciliarios/${courierId}/cuentas${id ? `/${id}` : ''}`;
      const { data } = id
        ? await api.patch<CuentaDomiciliario>(ruta, datos)
        : await api.post<CuentaDomiciliario>(ruta, datos);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'cuentas', courierId] }),
  });
}

export function useSubirQr(courierId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, archivo }: { id: string; archivo: File }) => {
      const datos = new FormData();
      datos.append('imagen', archivo);
      const { data } = await api.post<CuentaDomiciliario>(
        `/pagos/domiciliarios/${courierId}/cuentas/${id}/qr`,
        datos,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'cuentas', courierId] }),
  });
}

export function useEliminarCuenta(courierId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/pagos/domiciliarios/${courierId}/cuentas/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'cuentas', courierId] }),
  });
}

// ── Estado de la plataforma (interruptor) ─────────────────

export interface EstadoPlataforma {
  platformOpen: boolean;
  platformClosedMessage: string;
}

/**
 * ¿Está encendida la plataforma? Es la misma consulta pública de la tienda:
 * el comercio la necesita para saber si puede pedir un domiciliario. Se
 * refresca cada minuto y al volver a la pestaña.
 */
export function useEstadoPlataforma() {
  return useQuery({
    queryKey: ['plataforma-estado'],
    queryFn: async () => (await api.get<EstadoPlataforma>('/public/ajustes')).data,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

// ── Envíos masivos del comercio ─────────────────────────────

export interface CotizacionLote {
  items: { total: number; distanceMeters: number; covered: boolean; zoneName: string | null }[];
  total: number;
}

export interface LoteDelComercio {
  id: string;
  notes: string | null;
  createdAt: string;
  total: number;
  sinAsignar: number;
  enCurso: number;
  entregados: number;
  cancelados: number;
  monto: number;
}

export function useCotizarLote(merchantId: string) {
  return useMutation({
    mutationFn: async (datos: { items: { location: LatLng; size?: ParcelSize }[]; size: ParcelSize }) =>
      (await api.post<CotizacionLote>(`/encomiendas/comercio/${merchantId}/lote/cotizar`, datos)).data,
  });
}

export function useCrearLote(merchantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (datos: Record<string, unknown>) =>
      (
        await api.post<{
          id: string;
          items: { id: string; code: string; total: number; position: number }[];
          total: number;
        }>(`/encomiendas/comercio/${merchantId}/lote`, datos)
      ).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['comercio', merchantId, 'envios'] });
      void qc.invalidateQueries({ queryKey: ['comercio', merchantId, 'lotes'] });
    },
  });
}

export function useLotesDelComercio(merchantId: string) {
  return useQuery({
    queryKey: ['comercio', merchantId, 'lotes'],
    queryFn: async () =>
      (await api.get<LoteDelComercio[]>(`/encomiendas/comercio/${merchantId}/lotes`)).data,
    refetchInterval: 60_000,
  });
}

// ── Asignación manual (administración) ──────────────────────

export interface LoteAdmin extends LoteDelComercio {
  merchantId: string;
  merchantName: string;
}

export interface EnvioManual {
  id: string;
  code: string;
  status: OrderStatus;
  batchId: string | null;
  batchPosition: number | null;
  total: number;
  distanceMeters: number;
  pickupName: string;
  pickupAddress: { addressLine: string; details: string | null };
  deliveryAddress: { addressLine: string; details: string | null };
  customerNotes: string | null;
  cancelReason: string | null;
  courierId: string | null;
  courierName: string | null;
  createdAt: string;
  deliveredAt: string | null;
  size: ParcelSize;
  description: string;
  recipientName: string;
  recipientPhone: string;
  storeName: string | null;
  purchaseDetails: string | null;
  purchaseAmount: number | null;
  customerName: string;
  customerPhone: string | null;
}

export interface DomiciliarioParaAsignar {
  id: string;
  fullName: string;
  status: CourierStatus;
  vehicleType: string;
  activeOrders: number;
  enLote: boolean;
  balance: number;
  locationUpdatedAt: string | null;
}

export function useManualesPendientes(activo: boolean) {
  return useQuery({
    queryKey: ['admin', 'manuales', 'pendientes'],
    queryFn: async () =>
      (await api.get<{ lotes: number; noAliados: number }>('/admin/manuales/pendientes')).data,
    enabled: activo,
    refetchInterval: 60_000,
  });
}

export function useLotesAdmin(todos: boolean) {
  return useQuery({
    queryKey: ['admin', 'manuales', 'lotes', todos],
    queryFn: async () =>
      (await api.get<LoteAdmin[]>('/admin/manuales/lotes', { params: { todos } })).data,
    refetchInterval: 30_000,
  });
}

export function useLoteAdmin(id: string | null) {
  return useQuery({
    queryKey: ['admin', 'manuales', 'lote', id],
    queryFn: async () =>
      (
        await api.get<LoteAdmin & { merchantAddress: string; envios: EnvioManual[] }>(
          `/admin/manuales/lotes/${id}`,
        )
      ).data,
    enabled: !!id,
    refetchInterval: 20_000,
  });
}

export function useNoAliadosAdmin(todos: boolean) {
  return useQuery({
    queryKey: ['admin', 'manuales', 'no-aliados', todos],
    queryFn: async () =>
      (await api.get<EnvioManual[]>('/admin/manuales/no-aliados', { params: { todos } })).data,
    refetchInterval: 30_000,
  });
}

export function useDomiciliariosParaAsignar() {
  return useQuery({
    queryKey: ['admin', 'manuales', 'domiciliarios'],
    queryFn: async () =>
      (await api.get<DomiciliarioParaAsignar[]>('/admin/manuales/domiciliarios')).data,
    refetchInterval: 30_000,
  });
}

function refrescarManuales(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['admin', 'manuales'] });
  void qc.invalidateQueries({ queryKey: ['admin', 'domiciliarios'] });
}

export function useAsignarManual() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (datos: { orderIds: string[]; courierId: string }) =>
      (
        await api.post<{ asignados: number; omitidos: { id: string; motivo: string }[] }>(
          '/admin/manuales/asignar',
          datos,
        )
      ).data,
    onSuccess: () => refrescarManuales(qc),
  });
}

export function useLiberarManual() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (datos: { orderIds: string[] }) =>
      (
        await api.post<{ liberados: number; omitidos: { id: string; motivo: string }[] }>(
          '/admin/manuales/liberar',
          datos,
        )
      ).data,
    onSuccess: () => refrescarManuales(qc),
  });
}

// ── Avisos a todos los domiciliarios ───────────────────────

export interface AvisoDomiciliarios {
  id: string;
  title: string;
  body: string;
  recipients: number;
  senderName: string | null;
  createdAt: string;
}

export function useAvisosDomiciliarios() {
  return useQuery({
    queryKey: ['admin', 'avisos-domiciliarios'],
    queryFn: async () =>
      (await api.get<AvisoDomiciliarios[]>('/admin/domiciliarios/avisos')).data,
  });
}

export function useEnviarAvisoDomiciliarios() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (datos: { title: string; body: string }) =>
      (await api.post<{ id: string; recipients: number }>('/admin/domiciliarios/avisos', datos))
        .data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'avisos-domiciliarios'] }),
  });
}

// ── Saldo prepago de los domiciliarios ─────────────────────

export interface RecargaAdmin {
  id: string;
  declaredAmount: number;
  approvedAmount: number | null;
  receiptUrl: string;
  status: RechargeStatus;
  reviewNote: string | null;
  reviewedAt: string | null;
  createdAt: string;
}

export interface MovimientoSaldoAdmin {
  id: string;
  type: string;
  amount: number;
  balanceAfter: number;
  note: string | null;
  orderCode: string | null;
  createdAt: string;
}

export interface FichaSaldo {
  balance: number;
  cutPercent: number;
  rechargeAccountInfo: string;
  recargas: RecargaAdmin[];
  movimientos: MovimientoSaldoAdmin[];
}

export interface RecargaPendiente {
  id: string;
  courierId: string;
  courierName: string;
  declaredAmount: number;
  receiptUrl: string;
  createdAt: string;
}

export function useSaldoDomiciliario(courierId: string | null) {
  return useQuery({
    queryKey: ['admin', 'saldo', courierId],
    queryFn: async () => (await api.get<FichaSaldo>(`/admin/domiciliarios/${courierId}/saldo`)).data,
    enabled: !!courierId,
  });
}

/** Las recargas esperando revisión, de todos: alimenta la marca del menú. */
export function useRecargasPendientes(activo: boolean) {
  return useQuery({
    queryKey: ['admin', 'recargas-pendientes'],
    queryFn: async () => (await api.get<RecargaPendiente[]>('/admin/recargas/pendientes')).data,
    enabled: activo,
    refetchInterval: 60_000,
  });
}

function refrescarSaldos(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['admin', 'saldo'] });
  void qc.invalidateQueries({ queryKey: ['admin', 'recargas-pendientes'] });
  void qc.invalidateQueries({ queryKey: ['admin', 'domiciliarios'] });
}

export function useRevisarRecarga() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...datos
    }: {
      id: string;
      approve: boolean;
      amount?: number;
      note?: string;
    }) => (await api.post(`/admin/recargas/${id}/revisar`, datos)).data,
    onSuccess: () => refrescarSaldos(qc),
  });
}

export function useAjustarSaldo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ courierId, ...datos }: { courierId: string; amount: number; note: string }) =>
      (await api.post(`/admin/domiciliarios/${courierId}/saldo/ajuste`, datos)).data,
    onSuccess: () => refrescarSaldos(qc),
  });
}

export function useVerificarDomiciliario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      isVerified,
      reason,
    }: {
      id: string;
      isVerified: boolean;
      reason?: string;
    }) => (await api.patch(`/admin/domiciliarios/${id}/verificacion`, { isVerified, reason })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'domiciliarios'] }),
  });
}

export function useOperacion(enVivo = false, habilitado = true) {
  return useQuery({
    queryKey: clavesAdmin.operacion(),
    // Un administrador sin permiso de operación no pregunta: la API diría 403.
    enabled: habilitado,
    queryFn: async () => {
      const [pedidos, resumen] = await Promise.all([
        api.get<PedidoOperacion[]>('/admin/operacion'),
        api.get<ResumenOperacion>('/admin/operacion/resumen'),
      ]);
      return { pedidos: pedidos.data, resumen: resumen.data };
    },
    // Las alertas dependen del reloj: aunque no llegue ningún evento, un
    // pedido cruza el umbral de «demorado» por el simple paso del tiempo.
    refetchInterval: enVivo ? 30_000 : 10_000,
  });
}

export function useDisponibles() {
  return useQuery({
    queryKey: ['admin', 'disponibles'],
    queryFn: async () =>
      (
        await api.get<
          {
            id: string;
            fullName: string;
            vehicleType: string;
            ratingAvg: string;
            /** Cuántos lleva encima: a igual cercanía, mejor el más vacío. */
            activeOrders: number;
          }[]
        >('/admin/domiciliarios/disponibles')
      ).data,
  });
}

export function useIntervenirPedido() {
  const qc = useQueryClient();
  const invalidar = (): void => {
    void qc.invalidateQueries({ queryKey: clavesAdmin.operacion() });
    void qc.invalidateQueries({ queryKey: ['admin', 'disponibles'] });
  };

  return {
    reasignar: useMutation({
      mutationFn: async ({
        id,
        courierId,
        reason,
      }: {
        id: string;
        courierId: string;
        reason: string;
      }) => (await api.post(`/admin/operacion/pedidos/${id}/reasignar`, { courierId, reason })).data,
      onSuccess: invalidar,
    }),
    reintentar: useMutation({
      mutationFn: async (id: string) =>
        (await api.post(`/admin/operacion/pedidos/${id}/reintentar-asignacion`)).data,
      onSuccess: invalidar,
    }),
    cancelar: useMutation({
      mutationFn: async ({ id, reason }: { id: string; reason: string }) =>
        (await api.post(`/admin/operacion/pedidos/${id}/cancelar`, { reason })).data,
      onSuccess: invalidar,
    }),
  };
}

export function useAuditoria(filtros: { action?: string; entityType?: string }) {
  return useQuery({
    queryKey: clavesAdmin.auditoria(filtros),
    queryFn: async () => {
      const params = Object.fromEntries(
        Object.entries({ ...filtros, limit: 50 }).filter(([, v]) => v !== undefined && v !== ''),
      );
      return (
        await api.get<{ data: RenglonAuditoria[]; hasMore: boolean }>('/auditoria', { params })
      ).data;
    },
  });
}

export function useAccionesAuditoria() {
  return useQuery({
    queryKey: ['admin', 'auditoria', 'acciones'],
    queryFn: async () => (await api.get<string[]>('/auditoria/acciones')).data,
    staleTime: 5 * 60 * 1000,
  });
}

type AccionPedido =
  | { accion: 'aceptar'; prepMinutes?: number }
  | { accion: 'rechazar'; reason: string }
  | { accion: 'estado'; status: OrderStatus };

/** Aceptar, rechazar o avanzar el pedido; todo invalida el tablero. */
export function useAccionPedido(merchantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...datos }: { id: string } & AccionPedido) => {
      const ruta = datos.accion === 'estado' ? 'estado' : datos.accion;
      const cuerpo =
        datos.accion === 'aceptar'
          ? { prepMinutes: datos.prepMinutes }
          : datos.accion === 'rechazar'
            ? { reason: datos.reason }
            : { status: datos.status };
      return (await api.patch<PedidoComercio>(`/pedidos/${id}/${ruta}`, cuerpo)).data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pedidos', merchantId] });
    },
  });
}

/**
 * Reclamos sin resolver, para la portada de administración.
 *
 * Solo hace falta cuántos hay, pero el endpoint ya devuelve la lista y añadir
 * uno que solo cuente sería otra ruta que mantener por un número. La bandeja
 * rara vez pasa de unas decenas.
 */
export function useReclamosAbiertos(habilitado = true) {
  return useQuery({
    queryKey: ['admin', 'reclamos-abiertos'],
    enabled: habilitado,
    queryFn: async () => (await api.get<{ id: string }[]>('/admin/reclamos?limit=100')).data,
    refetchInterval: 60_000,
  });
}

/**
 * Conversaciones de soporte con algo sin leer, para la insignia del menú.
 *
 * Aquí sí hay endpoint propio, al revés que en reclamos: esto no se consulta
 * desde una pantalla sino desde la barra lateral, o sea desde **todas** las
 * pantallas y cada minuto. Traer la bandeja entera —con su subconsulta de no
 * leídos por fila— para quedarse con un número sería pagarlo en cada latido.
 */
/** Para el comercio: cuántas respuestas de soporte tiene sin leer. */
export function useSoporteSinLeerComercio(activo: boolean) {
  return useQuery({
    queryKey: ['soporte', 'sin-leer'],
    queryFn: async () => (await api.get<{ total: number }>('/soporte/chat/sin-leer')).data.total,
    enabled: activo,
    refetchInterval: 120_000,
  });
}

export function useSoporteSinLeer(activo: boolean) {
  return useQuery({
    queryKey: ['admin', 'soporte-sin-leer'],
    queryFn: async () => (await api.get<{ total: number }>('/admin/soporte/sin-leer')).data.total,
    enabled: activo,
    refetchInterval: 60_000,
  });
}

// ── Encomiendas programadas ───────────────────────────────

export interface EncomiendaProgramada {
  orderId: string;
  code: string;
  total: number;
  quotedTotal: number | null;
  repricedTotal: number | null;
  scheduledFor: string;
  approval: ParcelApproval;
  decisionNote: string | null;
  size: ParcelSize;
  description: string;
  declaredValue: number | null;
  payer: ParcelPayer;
  recipientName: string;
  recipientPhone: string;
  pickupAddress: { addressLine: string; details: string | null };
  deliveryAddress: { addressLine: string; details: string | null };
  distanceMeters: number;
  customerId: string;
  customerName: string;
  customerUsername: string;
  status: OrderStatus;
}

export function useEncomiendasProgramadas(todas: boolean) {
  return useQuery({
    queryKey: ['admin', 'encomiendas-programadas', todas],
    queryFn: async () =>
      (
        await api.get<EncomiendaProgramada[]>(
          `/encomiendas/programadas${todas ? '?todas=true' : ''}`,
        )
      ).data,
    refetchInterval: 60_000,
  });
}

/**
 * Cuántas esperan decisión, para la insignia del menú.
 *
 * Se deriva de la misma lista en vez de un endpoint aparte: son las pendientes
 * y rara vez pasan de unas pocas, así que no compensa otra ruta que mantener.
 */
export function useEncomiendasPendientes(activo: boolean) {
  return useQuery({
    queryKey: ['admin', 'encomiendas-programadas', false],
    queryFn: async () =>
      (await api.get<EncomiendaProgramada[]>('/encomiendas/programadas')).data,
    enabled: activo,
    refetchInterval: 60_000,
  });
}

export function useDecidirEncomienda() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orderId,
      approve,
      note,
    }: {
      orderId: string;
      approve: boolean;
      note?: string;
    }) => (await api.post(`/encomiendas/${orderId}/decision`, { approve, note })).data,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['admin', 'encomiendas-programadas'] }),
  });
}

/** Sacarla ya, sin esperar su hora: el cliente llamó y la adelantó. */
export function useDespacharEncomienda() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (orderId: string) => {
      await api.post(`/encomiendas/${orderId}/despachar`);
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['admin', 'encomiendas-programadas'] }),
  });
}

// ── Domiciliarios que pide el comercio ────────────────────

export interface EnvioDelComercio {
  orderId: string;
  code: string;
  status: OrderStatus;
  total: number;
  distanceMeters: number;
  deliveryAddress: { addressLine: string; details: string | null };
  createdAt: string;
  deliveredAt: string | null;
  scheduledFor: string | null;
  approval: ParcelApproval | null;
  decisionNote: string | null;
  size: ParcelSize;
  description: string;
  recipientName: string;
  recipientPhone: string;
}

export interface CotizacionEnvio {
  distanceMeters: number;
  deliveryFee: number;
  sizeFee: number;
  /** Tarifa de lluvia, si está encendida; ya va dentro de `total`. */
  rainFee: number;
  total: number;
  zoneName: string | null;
  covered: boolean;
  estimatedMinutes: number;
}

export function useEnviosDelComercio(merchantId: string) {
  return useQuery({
    queryKey: ['comercio', merchantId, 'envios'],
    queryFn: async () =>
      (await api.get<EnvioDelComercio[]>(`/encomiendas/comercio/${merchantId}`)).data,
    refetchInterval: 60_000,
  });
}

/**
 * El precio del envío, recalculado al mover el punto o el tamaño.
 *
 * El origen no viaja: lo pone el servidor con la dirección del comercio. Que
 * el navegador pudiera elegirlo permitiría mandar a alguien a cualquier parte
 * a nombre de la tienda.
 */
export function useCotizarEnvio(merchantId: string, destino: LatLng | null, size: ParcelSize) {
  return useQuery({
    queryKey: ['comercio', merchantId, 'envios', 'cotizacion', destino?.lat, destino?.lng, size],
    queryFn: async () =>
      (
        await api.post<CotizacionEnvio>(`/encomiendas/comercio/${merchantId}/cotizar`, {
          destination: destino,
          size,
        })
      ).data,
    enabled: !!destino,
    staleTime: 60_000,
  });
}

export function usePedirEnvio(merchantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (datos: Record<string, unknown>) =>
      (await api.post<{ id: string; code: string }>(`/encomiendas/comercio/${merchantId}`, datos))
        .data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comercio', merchantId, 'envios'] }),
  });
}

// ── Cuenta con la plataforma ──────────────────────────────

export interface AcumuladoDelMes {
  plan: BillingPlan;
  commissionRate: number;
  /** Desde el último corte; nulo si nunca ha habido uno (debe desde el inicio). */
  desde: string | null;
  ordersCount: number;
  grossAmount: number;
  commissionAmount: number;
  /** La cuota de todos los meses pendientes, no la de uno. */
  monthlyFee: number;
  /** Cuántos meses de cuota van en monthlyFee. */
  feeMonths: number;
  deliveriesRequested: number;
  deliveryCharges: number;
  amountDue: number;
  /** Lo que costaría con el otro plan, para poder comparar con un número. */
  conElOtroPlan: number;
}

export interface EstadoDeCuenta {
  id: string;
  periodStart: string;
  periodEnd: string;
  ordersCount: number;
  grossAmount: number;
  commissionAmount: number;
  monthlyFee: number;
  deliveryCharges: number;
  deliveriesRequested: number;
  amountDue: number;
  paidAmount: number;
  dueDate: string | null;
  status: SettlementStatus;
  paidAt: string | null;
  notes: string | null;
}

export interface CuentaDelComercio {
  acumulado: AcumuladoDelMes;
  estados: EstadoDeCuenta[];
  pendiente: number;
}

export function useCuentaDelComercio(merchantId: string) {
  return useQuery({
    queryKey: ['comercio', merchantId, 'cuenta'],
    queryFn: async () =>
      (await api.get<CuentaDelComercio>(`/merchants/${merchantId}/cuenta`)).data,
    refetchInterval: 5 * 60 * 1000,
    // El modal de la cuenta vive montado sin comercio elegido: sin esto
    // dispararía una consulta a /merchants//cuenta que solo puede dar 404.
    enabled: !!merchantId,
  });
}

export interface CobroDeComercio {
  id: string;
  name: string;
  status: MerchantStatus;
  plan: BillingPlan;
  monthlyFee: number | null;
  commissionRate: number;
  suspensionReason: string | null;
  /** Desde el último corte. */
  acumulado: number;
  /** De cortes anteriores sin pagar. */
  pendiente: number;
  /** Todo junto: lo que debe ahora mismo. */
  deuda: number;
  billedUntil: string | null;
  vencido: boolean;
}

export function usePanoramaDeCobros(activo = true) {
  return useQuery({
    queryKey: ['admin', 'cobros'],
    queryFn: async () => (await api.get<CobroDeComercio[]>('/admin/cobros')).data,
    refetchInterval: 60_000,
    // En Comercios solo se consulta si quien mira tiene el permiso de cobros.
    enabled: activo,
  });
}

export function useCambiarPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      merchantId,
      plan,
      monthlyFee,
    }: {
      merchantId: string;
      plan: BillingPlan;
      monthlyFee?: number;
    }) => (await api.patch(`/admin/cobros/${merchantId}/plan`, { plan, monthlyFee })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'cobros'] }),
  });
}

export function useRegistrarPago() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      settlementId,
      ...datos
    }: {
      settlementId: string;
      amount: number;
      method: string;
      reference?: string;
      notes?: string;
    }) => (await api.post(`/admin/cobros/estados/${settlementId}/pagos`, datos)).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'cobros'] });
      void qc.invalidateQueries({ queryKey: ['comercio'] });
    },
  });
}

/**
 * Un pago del comercio contra toda su cuenta.
 *
 * Hace el corte y aplica lo recibido del corte más viejo al más nuevo: si
 * salda todo queda en cero, si abona queda escrito cuánto falta.
 */
export function useRegistrarPagoComercio() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      merchantId,
      ...datos
    }: {
      merchantId: string;
      amount: number;
      method: string;
      reference?: string;
      notes?: string;
    }) =>
      (
        await api.post<{ total: number; saldoRestante: number; alDia: boolean }>(
          `/admin/cobros/${merchantId}/pagos`,
          datos,
        )
      ).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'cobros'] });
      void qc.invalidateQueries({ queryKey: ['comercio'] });
      void qc.invalidateQueries({ queryKey: ['liquidacion'] });
    },
  });
}

// ── Clasificación del mes ─────────────────────────────────

export interface Clasificacion {
  thresholds: {
    premiumOrdersPerMonth: number;
    premiumDiscountPercent: number;
    courierBonusDeliveries: number;
    courierBonusAmount: number;
  };
  clientes: {
    userId: string;
    fullName: string;
    username: string;
    ordersCount: number;
    spent: number;
    premium: boolean;
  }[];
  mensajeros: {
    courierId: string;
    fullName: string;
    deliveriesCount: number;
    earned: number;
    bono: boolean;
  }[];
}

export function useClasificacion() {
  return useQuery({
    queryKey: ['admin', 'clasificacion'],
    queryFn: async () => (await api.get<Clasificacion>('/admin/clasificacion')).data,
    refetchInterval: 5 * 60 * 1000,
  });
}

// ── Equipo de administración ──────────────────────────────

export interface AdministradorEquipo {
  id: string;
  username: string;
  fullName: string;
  email: string | null;
  phone: string | null;
  status: UserStatus;
  mustChangePassword: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  /** `null` es acceso total. */
  adminPermissions: PermisosAdmin | null;
}

export function useEquipo() {
  return useQuery({
    queryKey: ['admin', 'equipo'],
    queryFn: async () => (await api.get<AdministradorEquipo[]>('/admin/equipo')).data,
  });
}

export function useCrearAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (datos: {
      username: string;
      fullName: string;
      email?: string;
      phone?: string;
      permissions: PermisosAdmin | null;
    }) =>
      (
        await api.post<{ id: string; username: string; credenciales: Credenciales }>(
          '/admin/equipo',
          datos,
        )
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'equipo'] }),
  });
}

export function useCambiarPermisosAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, permissions }: { id: string; permissions: PermisosAdmin | null }) =>
      (await api.patch(`/admin/equipo/${id}/permisos`, { permissions })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'equipo'] }),
  });
}

export function useCambiarEstadoAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: UserStatus }) =>
      (await api.patch(`/admin/equipo/${id}/estado`, { status })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'equipo'] }),
  });
}

// ── Pedido por lista (comercio) ─────────────────────────────

export interface ItemLista {
  id: string;
  position: number;
  name: string;
  quantity: number;
  unit: string;
  note: string | null;
  allowSubstitute: boolean;
  status: 'PENDING' | 'CONFIRMED' | 'UNAVAILABLE';
  price: number | null;
  suggestionName: string | null;
  suggestionPrice: number | null;
  suggestionDecision: 'ACCEPTED' | 'REJECTED' | null;
}

export interface ListaDelPedido {
  orderId: string;
  code: string;
  stage: 'QUOTING' | 'AWAITING_APPROVAL' | 'APPROVED';
  budgetCap: number | null;
  subtotal: number;
  deliveryFee: number;
  serviceFee: number;
  total: number;
  pendientes: number;
  sinDecidir: number;
  items: ItemLista[];
}

export function useListaDelPedidoComercio(orderId: string | null) {
  return useQuery({
    queryKey: ['pedido-lista', orderId],
    queryFn: async () => (await api.get<ListaDelPedido>(`/pedidos/${orderId}/lista`)).data,
    enabled: !!orderId,
    // El socket avisa cada cambio; el sondeo es la red de seguridad y sigue
    // activo aunque la ventana del panel no tenga el foco.
    refetchInterval: 7_000,
    refetchIntervalInBackground: true,
  });
}

export function useRevisarRenglon(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      itemId,
      ...datos
    }: {
      itemId: string;
      status: 'CONFIRMED' | 'UNAVAILABLE';
      price?: number;
      suggestionName?: string;
      suggestionPrice?: number;
    }) => (await api.patch<ListaDelPedido>(`/pedidos/${orderId}/lista/${itemId}`, datos)).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pedido-lista', orderId] });
      void qc.invalidateQueries({ queryKey: ['pedidos'] });
    },
  });
}

export function useTerminarLista(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (await api.post<ListaDelPedido>(`/pedidos/${orderId}/lista/terminar`)).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pedido-lista', orderId] });
      void qc.invalidateQueries({ queryKey: ['pedidos'] });
    },
  });
}

/** Encender o apagar el pedido por lista de un comercio (solo administración). */
export function useCambiarPedidoPorLista() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ merchantId, listOrdering }: { merchantId: string; listOrdering: boolean }) =>
      (await api.patch(`/merchants/${merchantId}`, { listOrdering })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'comercios'] }),
  });
}
